import {
  Check,
  Gauge,
  Globe,
  Infinity as InfinityIcon,
  Network,
  ShieldCheck,
  Split,
  Users,
} from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  FormField,
  Input,
  Modal,
  PageHeader,
  Skeleton,
} from '@/components/ui';
import { useNotify } from '@/hooks/useNotify';
import { useT } from '@/i18n';
import { api, type Plan } from '@/lib/api';
import { cn } from '@/lib/cn';
import { formatPlanBandwidth, formatPrice } from '@/lib/format';
import { paths } from '@/router/paths';

export function PlansPage() {
  const t = useT();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Plan | null>(null);

  useEffect(() => {
    api.plans
      .list()
      .then(({ plans: list }) => setPlans(list))
      .catch((cause: Error) => setError(cause.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div className="mx-auto mb-8 max-w-2xl text-center">
        <h1 className="text-2xl font-extrabold text-t0 sm:text-[28px]">{t('plans.title')}</h1>
        <p className="mt-2 text-[13px] text-t1">{t('plans.subtitle')}</p>
      </div>

      {error && (
        <div className="mb-5 rounded-[var(--radius-vela-md)] border border-line bg-bad-soft px-4 py-3 text-[13px] font-medium text-bad">
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-[420px]" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {plans.map((plan, index) => (
            <PlanCard key={plan.id} plan={plan} featured={index === 1} onOrder={() => setSelected(plan)} />
          ))}
        </div>
      )}

      <p className="mt-6 text-center text-[11.5px] text-t2">{t('plans.footnote')}</p>

      <OrderDialog plan={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function PlanCard({
  plan,
  featured,
  onOrder,
}: {
  plan: Plan;
  featured: boolean;
  onOrder: () => void;
}) {
  const t = useT();
  const features = [
    { icon: Users, label: t('plans.clientsPerServer', { count: plan.maxPeers }) },
    {
      icon: Globe,
      label:
        plan.ipMode === 'DEDICATED_IPV4'
          ? t('plans.dedicatedIpv4Included')
          : t('plans.sharedIpv4'),
    },
    { icon: Network, label: t('plans.ipv6Included') },
    { icon: ShieldCheck, label: t('plans.ddos') },
    { icon: InfinityIcon, label: t('plans.unlimited') },
    plan.prioritySupport
      ? { icon: ShieldCheck, label: t('plans.prioritySupport') }
      : plan.allowSiteToSite
        ? { icon: Split, label: t('plans.siteToSite') }
        : { icon: Split, label: t('plans.splitTunnel') },
  ];

  return (
    <Card
      className={cn(
        'relative flex flex-col',
        featured && 'border-acc/50 ring-1 ring-acc/20',
      )}
    >
      {featured && (
        <Badge variant="accent" className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-acc text-white">
          {t('plans.popular')}
        </Badge>
      )}

      <h2 className="text-[13px] font-extrabold text-acc">{plan.name}</h2>
      <p className="mt-1 min-h-[34px] text-[11.5px] text-t2">{plan.description}</p>

      <p className="mt-3 text-[26px] font-extrabold text-t0">
        {formatPlanBandwidth(plan.bandwidthLimitMbps)}
        <span className="ml-1.5 text-[13px] font-medium text-t2">{t('plans.uplink')}</span>
      </p>

      <ul className="mt-4 space-y-2 rounded-[var(--radius-vela-md)] border border-line bg-bg-inset p-3">
        {features.map(({ icon: Icon, label }) => (
          <li key={label} className="flex items-center gap-2 text-[11.5px] text-t1">
            <Icon size={13} className="shrink-0 text-acc" />
            {label}
          </li>
        ))}
      </ul>

      <div className="mt-4">
        <p className="mb-2 flex items-center gap-1.5 text-[10.5px] font-bold tracking-wider text-t2 uppercase">
          <Gauge size={13} />
          {t('plans.networkUpgrade')}
        </p>
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="accent">
            {formatPlanBandwidth(plan.bandwidthLimitMbps)} {t('common.included')}
          </Badge>
          {plan.bandwidthTiers.map((tier) => (
            <Badge key={tier.mbps} variant="neutral">
              {formatPlanBandwidth(tier.mbps)} +{formatPrice(tier.priceCents, plan.currency)}
            </Badge>
          ))}
        </div>
      </div>

      <div className="mt-auto flex items-end justify-between border-t border-line pt-4 pt-5">
        <div>
          <p className="text-[10.5px] font-bold tracking-wider text-t2 uppercase">
            {t('plans.from')}
          </p>
          <p className="text-xl font-extrabold text-t0">
            {formatPrice(plan.priceCents, plan.currency)}
            <span className="text-[13px] font-medium text-t2">{t('common.perMonth')}</span>
          </p>
        </div>
        <Button onClick={onOrder}>{t('plans.order')}</Button>
      </div>
    </Card>
  );
}

function OrderDialog({ plan, onClose }: { plan: Plan | null; onClose: () => void }) {
  const navigate = useNavigate();
  const notify = useNotify();
  const t = useT();
  const [name, setName] = useState('');
  const [bandwidth, setBandwidth] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (plan) {
      setName(`${plan.name} Standort 1`);
      setBandwidth(plan.bandwidthLimitMbps);
      setError(null);
    }
  }, [plan]);

  if (!plan) return null;

  const options = [{ mbps: plan.bandwidthLimitMbps, priceCents: 0 }, ...plan.bandwidthTiers];
  const surcharge = options.find((option) => option.mbps === bandwidth)?.priceCents ?? 0;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const { instance } = await api.instances.create({
        planId: plan.id,
        name: name.trim(),
        bandwidthMbps: bandwidth ?? undefined,
      });
      notify.success(t('plans.ordered', { name: instance.name, ifName: instance.ifName }));
      onClose();
      navigate(paths.instances.detail(instance.id));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('plans.orderFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={t('plans.orderTitle', { plan: plan.name })}>
      <form onSubmit={submit} className="space-y-4">
        <p className="-mt-1 text-[12.5px] text-t2">{t('plans.orderSubtitle')}</p>

        <FormField label={t('plans.instanceName')} required hint={t('plans.instanceNameHint')}>
          <Input
            required
            maxLength={80}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </FormField>

        <div>
          <p className="mb-1.5 text-[12.5px] font-semibold text-t1">{t('plans.uplink')}</p>
          <div className="grid gap-2 sm:grid-cols-3">
            {options.map((option) => (
              <button
                key={option.mbps}
                type="button"
                onClick={() => setBandwidth(option.mbps)}
                className={cn(
                  'rounded-[var(--radius-vela-md)] border px-3 py-2.5 text-left transition-colors',
                  bandwidth === option.mbps
                    ? 'border-acc bg-acc-soft'
                    : 'border-line bg-bg-inset hover:border-line-2',
                )}
              >
                <span className="flex items-center justify-between">
                  <span className="text-[13px] font-bold text-t0">
                    {formatPlanBandwidth(option.mbps)}
                  </span>
                  {bandwidth === option.mbps && <Check size={13} className="text-acc" />}
                </span>
                <span className="mt-0.5 block text-[11px] text-t2">
                  {option.priceCents === 0
                    ? t('common.included')
                    : `+${formatPrice(option.priceCents, plan.currency)}${t('common.perMonth')}`}
                </span>
              </button>
            ))}
          </div>
        </div>

        <dl className="space-y-1.5 rounded-[var(--radius-vela-md)] border border-line bg-bg-inset p-3 text-[13px]">
          <div className="flex justify-between text-t1">
            <dt>{t('plans.basePrice')}</dt>
            <dd className="tabular-nums">{formatPrice(plan.priceCents, plan.currency)}</dd>
          </div>
          {surcharge > 0 && (
            <div className="flex justify-between text-t1">
              <dt>{t('plans.networkUpgrade')}</dt>
              <dd className="tabular-nums">+{formatPrice(surcharge, plan.currency)}</dd>
            </div>
          )}
          <div className="flex justify-between border-t border-line pt-1.5 font-bold text-t0">
            <dt>{t('plans.monthly')}</dt>
            <dd className="tabular-nums">{formatPrice(plan.priceCents + surcharge, plan.currency)}</dd>
          </div>
        </dl>

        {error && (
          <div className="rounded-[var(--radius-vela-md)] border border-line bg-bad-soft px-3 py-2 text-[13px] text-bad">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={busy}>
            {t('plans.orderConfirm')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
